import { useEffect } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaTimes } from 'react-icons/fa';

export type AlertToastType = 'success' | 'error';

interface AlertToastProps {
    type: AlertToastType;
    message: string;
    onClose: () => void;
    duration?: number;
}

/**
 * Lightweight, non-blocking notification — NOT a modal.
 * Appears in a corner, auto-dismisses, never blocks the page or requires interaction.
 */
const AlertToast: React.FC<AlertToastProps> = ({ type, message, onClose, duration = 5000 }) => {
    useEffect(() => {
        const timer = setTimeout(onClose, duration);
        return () => clearTimeout(timer);
    }, [onClose, duration]);

    const isSuccess = type === 'success';

    return (
        <div
            role="alert"
            className={`fixed top-4 right-4 left-4 sm:left-auto sm:max-w-sm z-[9999] flex items-start gap-3 px-4 py-3 rounded-xl shadow-lg border bg-light-default ${
                isSuccess ? 'border-accent-default' : 'border-red-400'
            }`}
        >
            {isSuccess ? (
                <FaCheckCircle className="mt-0.5 shrink-0 text-accent-default" />
            ) : (
                <FaExclamationCircle className="mt-0.5 shrink-0 text-red-600" />
            )}
            <p className="text-sm font-medium text-content-default flex-1">{message}</p>
            <button
                onClick={onClose}
                aria-label="Fermer"
                className="text-content-tertiary hover:text-content-default shrink-0"
            >
                <FaTimes size={12} />
            </button>
        </div>
    );
};

export default AlertToast;
